import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Plus, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";
import { API } from "@/lib/api";
import { getToken } from "@/lib/auth";

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function OptOutsTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [busy, setBusy] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newReason, setNewReason] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/optouts`, auth());
      setItems(r.data.items || []);
      setStats({ total: r.data.total || 0, active: r.data.active || 0 });
    } catch (e) {
      toast.error("Failed to load opt-outs");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function add() {
    if (!newPhone.trim()) return toast.error("Enter a phone number");
    try {
      await axios.post(`${API}/admin/optouts`,
        { phone: newPhone.trim(), reason: newReason.trim() || null },
        auth());
      toast.success("Phone added to opt-out registry");
      setNewPhone(""); setNewReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }

  async function remove(phone) {
    if (!confirm(`Opt ${phone} BACK IN to receive Spannr WhatsApp messages? Only do this if they explicitly asked.`)) return;
    try {
      await axios.delete(`${API}/admin/optouts/${encodeURIComponent(phone)}`, auth());
      toast.success("Opted back in");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  }

  return (
    <div data-testid="admin-optouts-tab" className="space-y-6">
      {/* Compliance banner */}
      <div className="bg-amber-50 border border-amber-200 p-4 text-sm">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-amber-900 mb-1">Meta WhatsApp compliance — automatic</div>
            <div className="text-amber-800 text-xs leading-relaxed">
              When a recipient replies <span className="font-mono font-bold">STOP</span>, <span className="font-mono font-bold">UNSUBSCRIBE</span>, <span className="font-mono font-bold">CANCEL</span>, <span className="font-mono font-bold">END</span> or <span className="font-mono font-bold">QUIT</span>, Spannr auto-registers them here, sends an unsubscribe confirmation, and blocks all future outbound (quotes, trial reminders, search resumes, bulk outreach). They can reply <span className="font-mono font-bold">START</span> or <span className="font-mono font-bold">UNSTOP</span> to opt back in.
            </div>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 gap-4">
        <Stat label="Active opt-outs" value={stats.active} icon={Ban} highlight />
        <Stat label="Total events (incl. opted back in)" value={stats.total} />
      </div>

      {/* Manual add */}
      <div className="border border-neutral-200 p-4">
        <div className="text-xs font-mono-tech tracking-[0.2em] text-neutral-500 mb-3">
          ADD MANUALLY (e.g. supplier emailed asking to be removed)
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="+27821234567 or 0821234567"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            data-testid="optout-phone-input"
            className="flex-1 min-w-[200px] border border-neutral-300 px-3 py-2 text-sm font-mono"
          />
          <input
            type="text"
            placeholder="Reason (optional)"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            data-testid="optout-reason-input"
            className="flex-1 min-w-[200px] border border-neutral-300 px-3 py-2 text-sm"
          />
          <button
            onClick={add}
            data-testid="optout-add-btn"
            className="bg-[#FF5100] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#e64a00] flex items-center gap-2"
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      {/* Registry table */}
      <div className="border border-neutral-200">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div className="text-xs font-mono-tech tracking-[0.2em] text-neutral-500">
            REGISTRY ({items.length})
          </div>
          <button onClick={load} disabled={busy} data-testid="optout-refresh-btn"
            className="text-xs flex items-center gap-1.5 text-neutral-600 hover:text-[#FF5100]">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            No opt-outs yet. (Good — means nobody has replied STOP.)
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Phone</th>
                  <th className="px-4 py-3 text-left font-bold">Opted out</th>
                  <th className="px-4 py-3 text-left font-bold">Source</th>
                  <th className="px-4 py-3 text-left font-bold">Raw text</th>
                  <th className="px-4 py-3 text-left font-bold">Status</th>
                  <th className="px-4 py-3 text-right font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const optedBackIn = !!it.opted_back_in_at;
                  return (
                    <tr key={it.phone + it.opted_out_at} className="border-t border-neutral-100">
                      <td className="px-4 py-3 font-mono">{it.phone}</td>
                      <td className="px-4 py-3 text-xs text-neutral-600">
                        {new Date(it.opted_out_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs">{it.source}</td>
                      <td className="px-4 py-3 text-xs text-neutral-500 max-w-[200px] truncate" title={it.raw_text}>
                        {it.raw_text || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {optedBackIn ? (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800">opted back in</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-800">DNC active</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!optedBackIn && (
                          <button
                            onClick={() => remove(it.phone)}
                            data-testid={`optout-remove-${it.phone}`}
                            className="text-xs text-neutral-500 hover:text-[#FF5100] inline-flex items-center gap-1"
                            title="Opt back in (admin override)"
                          >
                            <Trash2 size={12} /> Opt back in
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "border-[#FF5100] bg-[#FFF6F0]" : "border-neutral-200"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-neutral-500 mb-2 font-bold">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className={`text-3xl font-display font-extrabold ${highlight ? "text-[#FF5100]" : "text-neutral-900"}`}>
        {value}
      </div>
    </div>
  );
}
